import { Col, Form, FormInstance, InputNumber, Row, Tooltip } from 'antd';
import { FormValues } from './PriceValueCell';
import { OrderPrestataireLine } from '.';
import { CatalogPriceZoneSPPrice, CatalogPriceZoneSPPriceFamily } from 'hooks/catalog-price-zone-service-provider';
import { PriceType } from 'models';
import { PRICE_TYPE_LOGICS, PRICE_TYPES } from 'utils/constant';
import { useEffect, useMemo } from 'react';

interface GenerateCatalogPriceSectionProps {
  form: FormInstance;
  record: OrderPrestataireLine;
  uuid?: string;
  productId?: number;
  priceFamilies: CatalogPriceZoneSPPriceFamily[];
  priceTypes: PriceType[];
  selectedPriceFamilyId?: number | null;
  priceValue?: string | number;
  updateDataSource?: (data: OrderPrestataireLine, serviceProviderId?: number) => void;
}

/**
 * Initializes the unit price total with the price value of the product.
 * Then, it fetches parameter tarifs from the selected price family if available.
 * Finally, it calculates the total unit price by iterating over each parameter tarif.
 * @param {Object} params - Parameters to calculate unit price.
 * @param {FormInstance} params.form - Form instance.
 * @param {OrderPrestataireLine} params.record - Record of the order prestataire line.
 * @param {string} [params.uuid] - UUID of the record.
 * @param {number} [params.productId] - Product ID.
 * @param {CatalogPriceZoneSPPriceFamily[]} params.priceFamilies - List of price families.
 * @param {number} [params.selectedPriceFamilyId] - Selected price family ID.
 * @param {PriceType} [params.qtyPriceType] - Quantity price type.
 * @return {number} - Unit price total.
 */
export const getCalculateUnitPrix = (params: {
  form: FormInstance;
  record: OrderPrestataireLine;
  uuid?: string;
  productId?: number;
  priceFamilies: CatalogPriceZoneSPPriceFamily[];
  selectedPriceFamilyId?: number | null;
  qtyPriceType?: PriceType;
  isInitialData?: boolean;
}): number => {
  const {
    form,
    record,
    priceFamilies,
    selectedPriceFamilyId,
    qtyPriceType,
    uuid,
    productId,
    isInitialData = true,
  } = params;
  // Initialize the unit price total with the price value of the product.
  let unitPriceTotal = 0;

  // Fetch parameter tarifs from the selected price family if available.
  let parameterTarifs: CatalogPriceZoneSPPrice[] = [];
  if (priceFamilies.length > 0 && selectedPriceFamilyId) {
    const selectedPriceFamily = priceFamilies.find((priceFamily) => priceFamily.id === selectedPriceFamilyId);
    unitPriceTotal += parseFloat(selectedPriceFamily?.priceValue || '0');
    parameterTarifs = priceFamilies.find((priceFamily) => priceFamily.id === selectedPriceFamilyId)?.prices || [];
    // Calculate the total unit price by iterating over each parameter tarif.
    if (parameterTarifs.length > 0) {
      const calculateParameterTarifs = parameterTarifs.filter(
        (tarif) => tarif?.PriceTypeLogic?.key === PRICE_TYPE_LOGICS.calculate,
      );

      // For each tarif, calculate the total value based on price options.
      calculateParameterTarifs.forEach((tarif) => {
        const priceOptionValue = parseFloat(tarif?.priceValue || '0');

        // Calculate the product of priceValue for all priceOptions in this tarif.
        const calculatePriceOptions = tarif?.priceOptions?.filter(
          (priceOption) => priceOption?.priceTypeId === qtyPriceType?.id,
        );

        let priceOptionValueTotal = 0;
        if (calculatePriceOptions && calculatePriceOptions.length > 1) {
          // Calculate the product of priceValue for all priceOptions in this tarif.
          priceOptionValueTotal = calculatePriceOptions.reduce((accumulator, priceOption, index) => {
            const unitValue = priceOption?.priceValue ? parseFloat(priceOption?.priceValue || '0') : 0;
            return index === 0 ? unitValue : accumulator * unitValue;
          }, 1);
        } else if (calculatePriceOptions && calculatePriceOptions.length === 1) {
          // Calculate the product of priceValue for all priceOptions in this tarif.
          const unitValue = calculatePriceOptions[0]?.priceValue
            ? parseFloat(calculatePriceOptions[0]?.priceValue || '0')
            : 0;
          priceOptionValueTotal = unitValue;
        }

        // Multiply this product with the tarif's value.
        priceOptionValueTotal *= priceOptionValue;

        // Add this tarif's calculated value to the total.
        unitPriceTotal += priceOptionValueTotal;
      });
    }
  } else {
    const selectedProduct = record?.products.find(
      (product) => product.productId === productId && product.uuid === uuid,
    );
    if (selectedProduct) {
      unitPriceTotal = parseFloat(selectedProduct.priceValue.toString() || '0');
    }
  }
  // Update the form's unit price if it differs from the calculated unit price.
  if (
    `${form.getFieldValue(['lineItems', `${record?.serviceProviderId}`, 'products', `${uuid + '-' + productId}`, 'prixUnitaire'])}` !==
      `${unitPriceTotal}` &&
    isInitialData
  ) {
    form.setFieldsValue({
      lineItems: {
        [`${record?.serviceProviderId}`]: {
          products: {
            [`${uuid + '-' + productId}`]: {
              prixUnitaire: unitPriceTotal,
            },
          },
        },
      },
    });
  }

  return unitPriceTotal;
};

/**
 * Calculates the total unit price for a product line item in a logistique order.
 * The total unit price is the sum of the price family value and the calculated values
 * of all parameter tarifs with a calculate price type logic.
 * @param {Object} params - The parameters for calculating the total unit price.
 * @param {Object} params.form - The form instance.
 * @param {Object} params.formValuesWatch - The form values watch.
 * @param {Array} params.priceFamilies - The list of price families.
 * @param {Object} params.record - The record of the order prestataire line item.
 * @param {String} [params.uuid] - The UUID of the order prestataire line item.
 * @param {Number} [params.productId] - The ID of the product.
 * @param {Number} [params.selectedPriceFamilyId] - The ID of the selected price family.
 * @param {Object} [params.qtyPriceType] - The price type for quantity.
 * @returns {Number} The total unit price.
 */
export const handleCalculateUnitPrix = (params: {
  form: FormInstance;
  formValuesWatch: FormValues;
  priceFamilies: CatalogPriceZoneSPPriceFamily[];
  record: OrderPrestataireLine;
  uuid?: string;
  productId?: number;
  selectedPriceFamilyId?: number | null;
  qtyPriceType?: PriceType;
}): number => {
  const { form, formValuesWatch, record, priceFamilies, selectedPriceFamilyId, qtyPriceType, uuid, productId } = params;
  let totalUnitPrice = 0;

  const lineItem = formValuesWatch?.['lineItems']?.[`${record?.serviceProviderId}`];

  // Fetch parameter tarifs from the selected price family if available.
  let parameterTarifs: CatalogPriceZoneSPPrice[] = [];
  if (priceFamilies.length > 0 && selectedPriceFamilyId) {
    const productTarifs = lineItem?.['products']?.[`${uuid + '-' + productId}`]?.['tarifItems'];
    const priceFamilyValue = Number(productTarifs?.[`${selectedPriceFamilyId}`]?.['priceFamilyValue']) || 0;
    totalUnitPrice += priceFamilyValue;
    parameterTarifs = priceFamilies.find((priceFamily) => priceFamily.id === selectedPriceFamilyId)?.prices || [];

    if (parameterTarifs.length > 0) {
      const calculateParameterTarifs = parameterTarifs.filter(
        (tarif) => tarif?.PriceTypeLogic?.key === PRICE_TYPE_LOGICS.calculate,
      );

      // For each tarif, calculate the total value based on price options.
      calculateParameterTarifs.forEach((tarif) => {
        const productLinePrices = productTarifs?.['productLinePrices'];
        const priceOptionValue = Number(productLinePrices?.[`${tarif?.id}`]?.['priceValue']) || 0;

        // Calculate the product of priceValue for all priceOptions in this tarif.
        const calculatePriceOptions = tarif?.priceOptions?.filter(
          (priceOption) => priceOption?.priceTypeId === qtyPriceType?.id,
        );

        let priceOptionValueTotal = 0;
        if (calculatePriceOptions && calculatePriceOptions.length > 1) {
          // Calculate the product of priceValue for all priceOptions in this tarif.
          priceOptionValueTotal = calculatePriceOptions.reduce((accumulator, priceOption, index) => {
            const priceOptions = productLinePrices?.['priceOption'];
            const unitValue = Number(priceOptions?.[`${priceOption?.id}`]?.['priceOptionValue']) || 0;
            return index === 0 ? unitValue : accumulator * unitValue;
          }, 1);
        } else if (calculatePriceOptions && calculatePriceOptions.length === 1) {
          // Calculate the product of priceValue for all priceOptions in this tarif.
          const priceOptions = productLinePrices?.['priceOption'];
          const unitValue = Number(priceOptions?.[`${calculatePriceOptions[0]?.id}`]?.['priceOptionValue']) || 0;
          priceOptionValueTotal = unitValue;
        }

        // Multiply this product with the tarif's value.
        priceOptionValueTotal *= priceOptionValue;

        // Add this tarif's calculated value to the total.
        totalUnitPrice += priceOptionValueTotal;
      });
    }
  } else {
    const priceValue = lineItem?.['products']?.[`${uuid + '-' + productId}`]?.['prixUnitaire'] || '0';
    totalUnitPrice += Number(priceValue);
  }

  if (
    `${form.getFieldValue(['lineItems', `${record?.serviceProviderId}`, 'products', `${uuid + '-' + productId}`, 'prixUnitaire'])}` !==
    `${totalUnitPrice}`
  ) {
    form.setFieldsValue({
      lineItems: {
        [`${record?.serviceProviderId}`]: {
          products: {
            [`${uuid + '-' + productId}`]: {
              prixUnitaire: totalUnitPrice,
            },
          },
        },
      },
    });
  }
  return totalUnitPrice;
};

const GenerateCatalogPriceSection = (props: GenerateCatalogPriceSectionProps) => {
  const { form, record, uuid, productId, priceFamilies, priceTypes, selectedPriceFamilyId, priceValue, updateDataSource } = props;
  const formValuesWatch = Form.useWatch<FormValues>([], form);
  const qtyPriceType = priceTypes?.find((priceType) => priceType.key === PRICE_TYPES.numberInput);

  useEffect(() => {
    // Initial calculation of the unit price based on the form values and selection.
    getCalculateUnitPrix({
      form,
      record,
      uuid,
      productId,
      priceFamilies,
      selectedPriceFamilyId,
      qtyPriceType,
    });
  }, [uuid, productId]);

  // Dynamic recalculation on value change
  useMemo(() => {
    handleCalculateUnitPrix({
      form,
      formValuesWatch,
      record,
      uuid,
      productId,
      priceFamilies,
      selectedPriceFamilyId,
      qtyPriceType,
    });
  }, [formValuesWatch, selectedPriceFamilyId]);

  return (
    <section className='section generate-catalog-price-section'>
      <Row gutter={[16, 0]}>
        <Col xs={{ span: 18 }}>
          <Form.Item
            name={[
              'lineItems',
              `${record?.serviceProviderId}`,
              'products',
              `${uuid + '-' + productId}`,
              'prixUnitaire',
            ]}
            className='total-price'
            rules={[{ required: true }]}
          >
            <Tooltip
              title={(() => {
                const currentProduct = record.products.find(
                  (product) => product.uuid === uuid && product.productId === productId,
                );

                if (!priceValue || priceValue === 0 || priceValue === "0") {
                  return "Pas de prix";
                }

                const selectedFamily = currentProduct?.priceFamilies?.find((f) => f.id === selectedPriceFamilyId);
                const isManual =
                  currentProduct?.isManualPrice ||
                  selectedFamily?.isManualPrice ||
                  selectedFamily?.prices?.some(
                    (p) => p.isManualPrice || p.priceOptions?.some((opt) => opt.isManualPrice),
                  );

                return isManual ? "Prix manuel" : "Prix catalogue";
              })()}
              placement="left"
              align={{ offset: [0, 2] }}
              color="#A6C84D"
              overlayInnerStyle={{
                color: "rgba(0, 0, 0, 0.88)",
                fontWeight: 500,
              }}
            >
              <div style={{ display: "inline-block", width: "100%" }}>
                <Form.Item
                  name={[
                    "lineItems",
                    `${record.serviceProviderId}`,
                    "products",
                    `${uuid}-${productId}`,
                    "prixUnitaire",
                  ]}
                  rules={[{ required: true }]}
                  initialValue={priceValue ? Number(priceValue) : 0}
                  noStyle
                >
                  <InputNumber
                    className="input-prix-unitaire"
                    suffix="€"
                    formatter={(value) =>
                      value!
                        .toString()
                        .replace(/\s/g, "")
                        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")
                        .replace(".", ",")
                    }
                    parser={(value) =>
                      value!.replace(/\s/g, "").replace(/,/g, ".")
                    }
                    disabled={priceFamilies.length > 0}
                    onChange={(newValue) => {
                      if (updateDataSource) {
                        const updatedProducts = record.products.map(
                          (product) => {
                            if (
                              product.uuid === uuid &&
                              product.productId === productId
                            ) {
                              let isManual = false;
                              if (product.wasInitiallyManual) {
                                isManual = true;
                              } else {
                                const catalogValue = product.catalogPriceValue;
                                if (
                                  catalogValue !== undefined &&
                                  catalogValue !== null &&
                                  catalogValue !== ""
                                ) {
                                  isManual =
                                    Number(newValue) !== Number(catalogValue);
                                } else {
                                  isManual =
                                    newValue !== null &&
                                    newValue !== 0 &&
                                    newValue !== "0";
                                }
                              }

                              return {
                                ...product,
                                priceValue: newValue?.toString() ?? "",
                                isManualPrice: isManual,
                              };
                            }
                            return product;
                          }
                        );
                        updateDataSource(
                          {
                            ...record,
                            products: updatedProducts,
                          },
                          record.serviceProviderId
                        );
                      }
                      form.setFieldsValue({
                        lineItems: {
                          [`${record.serviceProviderId}`]: {
                            products: {
                              [`${uuid}-${productId}`]: {
                                prixUnitaire: newValue,
                              },
                            },
                          },
                        },
                      });
                    }}
                  />
                </Form.Item>
              </div>
            </Tooltip>
          </Form.Item>
        </Col>
      </Row>
    </section>
  );
};

export default GenerateCatalogPriceSection;
